import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Shield, PartyPopper } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DocumentDistributionPanel } from "@/components/workflows/DocumentDistributionPanel";
import { GovernanceFilingPanel } from "@/components/workflows/GovernanceFilingPanel";
import type { Recipient } from "@/components/workflows/types/workflow-components.types";

interface OfficerPhase3Props {
  taskId: string;
  unaId: string;
  task: any;
  phaseData: any;
  onPhaseComplete?: () => void;
}

export function OfficerPhase3({
  taskId,
  unaId,
  task,
  phaseData,
  onPhaseComplete,
}: OfficerPhase3Props) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [distributionComplete, setDistributionComplete] = useState(false);
  const [filingComplete, setFilingComplete] = useState(false);
  const [completingPhase, setCompletingPhase] = useState(false);
  const [officerRecipient, setOfficerRecipient] = useState<Recipient | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    loadDocuments();
    checkCompletionStatus();
    extractOfficerRecipient();
  }, [taskId]);

  // Check if task is already completed
  useEffect(() => {
    if (task?.status === 'completed') {
      setIsCompleted(true);
    }
  }, [task]);

  const extractOfficerRecipient = () => {
    try {
      const workflowData = task.workflow_data || {};
      
      // Extract officer info from workflow_data
      if (workflowData.officer_full_name && workflowData.officer_email) {
        const recipient: Recipient = {
          id: `officer-${Date.now()}`,
          name: workflowData.officer_full_name,
          email: workflowData.officer_email,
          type: 'external',
          role: `Newly Appointed ${workflowData.officer_role || 'Officer'}`
        };
        
        setOfficerRecipient(recipient);
        console.log('Officer recipient extracted:', recipient);
      } else {
        console.warn('Officer info not found in workflow_data');
      }
    } catch (error) {
      console.error('Error extracting officer recipient:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from("generated_documents")
        .select("*")
        .eq("task_id", taskId)
        .in("status", ["final", "finalized"])
        .order("generated_at");

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const checkCompletionStatus = async () => {
    await Promise.all([
      checkDistributionComplete(),
      checkFilingComplete(),
    ]);
  };

  const checkDistributionComplete = async () => {
    try {
      const { data, error } = await supabase
        .from("document_distribution_log")
        .select("id")
        .eq("task_id", taskId)
        .limit(1);

      if (error) throw error;
      setDistributionComplete((data?.length || 0) > 0);
    } catch (error) {
      console.error("Error checking distribution status:", error);
    }
  };

  const checkFilingComplete = async () => {
    try {
      // Filter out resolution documents - they go to Resolutions & Actions section
      const nonResolutionDocs = documents.filter(doc => 
        !doc.document_name?.toLowerCase().includes('resolution')
      );

      // If there are no non-resolution docs to file, consider filing complete
      if (nonResolutionDocs.length === 0) {
        setFilingComplete(true);
        return;
      }

      // Count filed documents
      const { count: filedDocs } = await supabase
        .from("governance_documents")
        .select("id", { count: "exact", head: true })
        .eq("generated_from_task_id", taskId);

      setFilingComplete((filedDocs || 0) >= 1);
    } catch (error) {
      console.error("Error checking filing status:", error);
    }
  };

  const handleDistributionComplete = async () => {
    await checkDistributionComplete();
  };

  const handleFilingComplete = async () => {
    await checkFilingComplete();
  };

  const handleMarkComplete = async () => {
    setCompletingPhase(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "complete-officer-appointment",
        {
          body: {
            taskId,
            unaId,
            workflowData: task.workflow_data,
          },
        }
      );

      if (error) throw error;

      setIsCompleted(true);
      toast.success(
        `Officer Appointment Complete! Created ${data.tasks_created} follow-up tasks.`
      );
      onPhaseComplete?.();
    } catch (error) {
      console.error("Error completing Phase 3:", error);
      toast.error("Failed to complete Phase 3");
    } finally {
      setCompletingPhase(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter documents: Resolution documents go to Resolutions & Actions automatically
  // Only non-resolution documents go to Document Repository via GovernanceFilingPanel
  const nonResolutionDocs = documents.filter(doc => 
    !doc.document_name?.toLowerCase().includes('resolution')
  );

  // Distribution is optional, only filing is required
  const allComplete = filingComplete;

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Phase 3: Distribution & Filing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Distribute officer appointment documents and file to governance records.
          </p>

          <Alert>
            <AlertDescription>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {distributionComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                  )}
                  <span className="text-sm">
                    Distribute documents to officer, members, and primary officer
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {filingComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                  )}
                  <span className="text-sm">
                    File all documents in governance records
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {allComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                  )}
                  <span className="text-sm">
                    Create officer appointment records
                  </span>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {officerRecipient && (
            <Alert className="border-blue-200 bg-blue-50">
              <Shield className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>{officerRecipient.name}</strong> will be included in the distribution list along with all UNA members.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Document Distribution Panel - with officer pre-populated */}
      <DocumentDistributionPanel
        taskId={taskId}
        unaId={unaId}
        documents={documents}
        onDistributionComplete={handleDistributionComplete}
        additionalRecipients={officerRecipient ? [officerRecipient] : []}
      />

      {/* Info about resolution routing */}
      <Alert className="border-blue-200 bg-blue-50">
        <AlertDescription className="text-blue-800 text-sm">
          <strong>Note:</strong> The Officer Appointment Resolution will be automatically 
          filed to the Resolutions & Actions section when you complete this workflow. 
          Other documents will be filed to the Document Repository below.
        </AlertDescription>
      </Alert>

      {/* Governance Filing Panel - only for non-resolution documents */}
      {nonResolutionDocs.length > 0 ? (
        <GovernanceFilingPanel
          taskId={taskId}
          unaId={unaId}
          documents={nonResolutionDocs}
          workflowKey="add_officer"
          onFilingComplete={handleFilingComplete}
        />
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground text-center">
              No additional documents require filing to Document Repository.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Complete Phase - Show completion state or button */}
      {isCompleted ? (
        <Alert className="border-green-200 bg-green-50">
          <PartyPopper className="h-5 w-5 text-green-600" />
          <AlertDescription>
            <div className="ml-2">
              <p className="font-medium text-green-800">Officer Appointment Completed</p>
              <p className="text-sm text-green-700">
                This workflow has been completed successfully. The officer has been appointed and records updated.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className={!allComplete ? 'border-muted' : 'border-green-200 bg-green-50'}>
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {allComplete ? 'All Phase 3 Steps Complete' : 'Complete Officer Appointment'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {allComplete 
                    ? `${distributionComplete ? 'Documents distributed and filed.' : 'Documents filed.'} Ready to complete workflow.`
                    : 'File at least one document to governance to complete. Distribution is optional.'
                  }
                </p>
              </div>
              <Button 
                onClick={handleMarkComplete}
                disabled={!allComplete || completingPhase}
              >
                {completingPhase ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  'Complete Officer Appointment'
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
